import './fomo-home-intro.css'
const INTRO_SESSION_KEY = 'fomo-home-intro-played'

/** Original FOMO particle intro, retained before the new QR sequence. */
export function createFomoHomeIntro(onComplete, { force = false } = {}) {
  let disposed = false,
    finished = false,
    overlay = null,
    finishTimer = null,
    fontTimer = null,
    removeListeners = () => {}
  const reduced = matchMedia('(prefers-reduced-motion: reduce)')
  let hasPlayed = false
  try {
    hasPlayed = sessionStorage.getItem(INTRO_SESSION_KEY) === '1'
  } catch {
    /* Storage can be unavailable. */
  }
  const dispose = () => {
    disposed = true
    clearTimeout(finishTimer)
    clearTimeout(fontTimer)
    removeListeners()
    state?.stopIntroAnimation()
    overlay?.remove()
  }
  const complete = () => {
    if (disposed || finished) return
    finished = true
    dispose()
    onComplete()
  }
  let state
  if (reduced.matches || (hasPlayed && !force)) {
    complete()
    return dispose
  }
  overlay = document.createElement('div')
  overlay.className = 'fomo-hero__intro'
  overlay.setAttribute('aria-hidden', 'true')
  const canvas = document.createElement('canvas')
  canvas.className = 'fomo-hero__intro-canvas'
  const subtitle = document.createElement('div')
  subtitle.className = 'fomo-hero__intro-subtitle'
  subtitle.textContent = 'Fear of Missing Out'
  overlay.append(canvas, subtitle)
  document.body.append(overlay)
  state = {
    $refs: { introCanvas: canvas },
    showIntroOverlay: true,
    introFrame: null,
    introStartedAt: 0,
    set introSubtitleVisible(value) {
      subtitle.classList.toggle('is-visible', value)
    },
    finishIntro(skipStorage = false) {
      this.stopIntroAnimation()
      this.showIntroOverlay = false
      if (skipStorage) {
        complete()
        return
      }
      try {
        sessionStorage.setItem(INTRO_SESSION_KEY, '1')
      } catch {
        /* Still finish if storage is blocked. */
      }
      overlay.classList.add('is-done')
      finishTimer = setTimeout(complete, 900)
    },
    startIntroAnimation() {
      const canvas = this.$refs.introCanvas
      if (!canvas) {
        this.finishIntro(true)
        return
      }

      const context = canvas.getContext('2d')
      if (!context) {
        this.finishIntro(true)
        return
      }

      const width = (canvas.width = window.innerWidth)
      const height = (canvas.height = window.innerHeight)

      const sampleText = (text, fontSize) => {
        const offscreen = document.createElement('canvas')
        offscreen.width = width
        offscreen.height = height
        const offscreenContext = offscreen.getContext('2d')

        if (!offscreenContext) {
          return []
        }

        offscreenContext.fillStyle = '#ffffff'
        offscreenContext.font = `900 ${fontSize}px "Bebas Neue", sans-serif`
        offscreenContext.textAlign = 'center'
        offscreenContext.textBaseline = 'middle'
        offscreenContext.fillText(text, width / 2, height / 2)

        const imageData = offscreenContext.getImageData(0, 0, width, height).data
        const points = []
        const step = Math.max(3, Math.floor(width / 120))

        for (let y = 0; y < height; y += step) {
          for (let x = 0; x < width; x += step) {
            const index = (y * width + x) * 4
            if (imageData[index + 3] > 128) {
              points.push({ x, y })
            }
          }
        }

        return points
      }

      const fontSize = Math.min(width * 0.28, height * 0.35, 280)
      const targets = sampleText('FOMO', fontSize)

      if (!targets.length) {
        this.finishIntro(true)
        return
      }

      const colors = [
        '#7C5CFC',
        '#9D7FFF',
        '#C4B5FD',
        '#E2DEFF',
        '#A8A0C8',
        '#D4D0E8',
        '#6040D0',
        '#B8B0D8',
      ]
      const particleCount = Math.min(targets.length, 600)
      const stride = Math.max(1, Math.floor(targets.length / particleCount))
      const shouldUseMobileTargetSampling = width <= 720 && targets.length > particleCount
      const chosenTargets = []

      for (let index = 0; index < particleCount; index += 1) {
        if (shouldUseMobileTargetSampling) {
          const targetIndex =
            particleCount === 1
              ? 0
              : Math.round((index / (particleCount - 1)) * (targets.length - 1))
          chosenTargets.push(targets[targetIndex])
        } else {
          chosenTargets.push(targets[index * stride] || targets[targets.length - 1])
        }
      }

      const particles = chosenTargets.map((target, index) => {
        const edge = index % 4
        let startX = 0
        let startY = 0

        if (edge === 0) {
          startX = -80 - Math.random() * 120
          startY = Math.random() * height
        } else if (edge === 1) {
          startX = width + 80 + Math.random() * 120
          startY = Math.random() * height
        } else if (edge === 2) {
          startX = Math.random() * width
          startY = -80 - Math.random() * 120
        } else {
          startX = Math.random() * width
          startY = height + 80 + Math.random() * 120
        }

        const dx = target.x - width / 2
        const dy = target.y - height / 2
        const distance = Math.sqrt(dx * dx + dy * dy) || 1
        const speed = 250 + Math.random() * 350

        return {
          sx: startX,
          sy: startY,
          tx: target.x,
          ty: target.y,
          x: startX,
          y: startY,
          r: Math.random() * 1.6 + 0.7,
          color: colors[Math.floor(Math.random() * colors.length)],
          ex: (dx / distance) * speed,
          ey: (dy / distance) * speed + (Math.random() - 0.5) * 180,
          alpha: 1,
        }
      })

      const gatherDuration = 1600
      const holdDuration = 600
      const explodeDuration = 700
      const totalDuration = gatherDuration + holdDuration + explodeDuration

      this.introStartedAt = 0
      this.introPhase = 'assemble'
      this.introProgress = 0
      this.introSubtitleVisible = false
      this.stopIntroAnimation()

      const render = (timestamp) => {
        if (!this.showIntroOverlay) {
          this.stopIntroAnimation()
          return
        }

        if (!this.introStartedAt) {
          this.introStartedAt = timestamp
        }

        const elapsed = timestamp - this.introStartedAt
        context.clearRect(0, 0, width, height)

        const holdStart = gatherDuration
        const holdEnd = gatherDuration + holdDuration
        const holdProgress =
          elapsed >= holdStart ? Math.min((elapsed - holdStart) / holdDuration, 1) : 0

        const ambientGlow = context.createRadialGradient(
          width * 0.5,
          height * 0.5,
          0,
          width * 0.5,
          height * 0.5,
          Math.max(width, height) * 0.32
        )
        ambientGlow.addColorStop(0, 'rgba(124, 92, 252, 0.08)')
        ambientGlow.addColorStop(0.45, 'rgba(157, 127, 255, 0.03)')
        ambientGlow.addColorStop(1, 'rgba(2, 1, 10, 0)')
        context.fillStyle = ambientGlow
        context.fillRect(0, 0, width, height)

        particles.forEach((particle) => {
          if (elapsed < gatherDuration) {
            const progress = this.easeOutQuint(Math.min(elapsed / gatherDuration, 1))
            particle.x = particle.sx + (particle.tx - particle.sx) * progress
            particle.y = particle.sy + (particle.ty - particle.sy) * progress
            particle.alpha = 0.4 + progress * 0.6
          } else if (elapsed < gatherDuration + holdDuration) {
            particle.x = particle.tx
            particle.y = particle.ty
            particle.alpha = 1
          } else {
            const progress = this.easeInCubic(
              Math.min((elapsed - gatherDuration - holdDuration) / explodeDuration, 1)
            )
            particle.x = particle.tx + particle.ex * progress
            particle.y = particle.ty + particle.ey * progress
            particle.alpha = 1 - progress
          }

          context.beginPath()
          context.arc(particle.x, particle.y, particle.r, 0, Math.PI * 2)
          context.fillStyle = particle.color
          context.globalAlpha = Math.max(0, particle.alpha)
          context.fill()
        })

        context.globalAlpha = 1

        if (elapsed >= holdStart && elapsed < holdEnd) {
          const revealProgress = Math.min(Math.max((holdProgress - 0.18) / 0.58, 0), 1)

          if (revealProgress > 0) {
            const textOpacity = 0.12 + revealProgress * 0.2

            context.save()
            context.textAlign = 'center'
            context.textBaseline = 'middle'
            context.font = `900 ${fontSize}px "Bebas Neue", sans-serif`
            context.fillStyle = `rgba(237, 232, 255, ${textOpacity})`
            context.shadowBlur = 26 + revealProgress * 36
            context.shadowColor = `rgba(124, 92, 252, ${0.14 + revealProgress * 0.2})`
            context.fillText('FOMO', width / 2, height / 2)
            context.restore()

            const pulse = (0.08 + Math.sin(revealProgress * Math.PI) * 0.12) * revealProgress
            const pulseGlow = context.createRadialGradient(
              width * 0.5,
              height * 0.5,
              0,
              width * 0.5,
              height * 0.5,
              fontSize * 1.1
            )
            pulseGlow.addColorStop(0, `rgba(196, 181, 253, ${pulse})`)
            pulseGlow.addColorStop(0.5, `rgba(124, 92, 252, ${pulse * 0.34})`)
            pulseGlow.addColorStop(1, 'rgba(2, 1, 10, 0)')
            context.fillStyle = pulseGlow
            context.fillRect(0, 0, width, height)

            const shimmerProgress = Math.min(Math.max((holdProgress - 0.44) / 0.42, 0), 1)
            if (shimmerProgress > 0) {
              const shimmerX = width * 0.5 - fontSize + fontSize * 2 * shimmerProgress
              const shimmer = context.createLinearGradient(shimmerX - 100, 0, shimmerX + 100, 0)
              shimmer.addColorStop(0, 'rgba(255, 255, 255, 0)')
              shimmer.addColorStop(0.48, 'rgba(255, 255, 255, 0.04)')
              shimmer.addColorStop(0.5, 'rgba(255, 255, 255, 0.34)')
              shimmer.addColorStop(0.52, 'rgba(196, 181, 253, 0.28)')
              shimmer.addColorStop(1, 'rgba(255, 255, 255, 0)')
              context.fillStyle = shimmer
              context.fillRect(shimmerX - 100, height * 0.22, 200, height * 0.56)
            }
          }
        }

        if (elapsed > gatherDuration + holdDuration * 0.3) {
          this.introSubtitleVisible = true
        }

        if (elapsed < totalDuration) {
          this.introFrame = window.requestAnimationFrame(render)
          return
        }

        this.finishIntro()
      }

      this.introFrame = window.requestAnimationFrame(render)
    },
    stopIntroAnimation() {
      if (this.introFrame) {
        window.cancelAnimationFrame(this.introFrame)
        this.introFrame = null
      }
    },
    easeOutQuint(value) {
      return 1 - Math.pow(1 - value, 5)
    },
    easeInCubic(value) {
      return value * value * value
    },
  }
  const onResize = () => {
    if (!disposed && state.showIntroOverlay && overlay.dataset.ready === 'true')
      state.startIntroAnimation()
  }
  const onReduce = () => {
    if (reduced.matches) complete()
  }
  window.addEventListener('resize', onResize)
  reduced.addEventListener('change', onReduce)
  removeListeners = () => {
    window.removeEventListener('resize', onResize)
    reduced.removeEventListener('change', onReduce)
  }
  const begin = () => {
    if (disposed || overlay.dataset.ready === 'true') return
    clearTimeout(fontTimer)
    overlay.dataset.ready = 'true'
    state.startIntroAnimation()
  }
  // A slow font request must never leave the original intro covering the home indefinitely.
  fontTimer = setTimeout(begin, 1200)
  Promise.resolve(document.fonts?.load('900 240px "Bebas Neue"'))
    .catch(() => {})
    .then(begin)
  return dispose
}
